// Channel-wide revenue, gross profit and GM% for the Plan vs Actual page, in
// total and per month.
//
// Only past months (before lockedBefore) count, from realized ProductSale
// money, so target ASP and COGS play no part. The current and future months
// are absent from the sums.

interface MarginCell {
  period_date: string;
  actual_revenue?: number | null;
  actual_cogs?: number | null;
}

interface MarginRow {
  months: MarginCell[];
}

export interface MarginTotals {
  revenue: number;
  grossProfit: number;
  // null when revenue is zero (margin undefined).
  gmPct: number | null;
}

export interface ChannelMargin extends MarginTotals {
  // Same sums split by period_date (YYYY-MM-01).
  byMonth: Map<string, MarginTotals>;
}

const gmPctOf = (revenue: number, grossProfit: number) => (revenue > 0 ? grossProfit / revenue : null);

export function channelMargin(rows: MarginRow[], lockedBefore: string): ChannelMargin {
  let revenue = 0;
  let grossProfit = 0;
  const monthSums = new Map<string, { revenue: number; grossProfit: number }>();

  for (const row of rows) {
    for (const cell of row.months) {
      if (cell.period_date >= lockedBefore) continue;
      const cellRevenue = cell.actual_revenue ?? 0;
      const cellProfit = cellRevenue - (cell.actual_cogs ?? 0);
      revenue += cellRevenue;
      grossProfit += cellProfit;
      const month = monthSums.get(cell.period_date) ?? { revenue: 0, grossProfit: 0 };
      month.revenue += cellRevenue;
      month.grossProfit += cellProfit;
      monthSums.set(cell.period_date, month);
    }
  }

  const byMonth = new Map<string, MarginTotals>();
  for (const [month, sums] of monthSums) {
    byMonth.set(month, { ...sums, gmPct: gmPctOf(sums.revenue, sums.grossProfit) });
  }

  return { revenue, grossProfit, gmPct: gmPctOf(revenue, grossProfit), byMonth };
}
